/* eslint-disable no-magic-numbers */
import {
  FileCard,
  notifyWarning,
  Stack,
  Typography,
} from '@omnipresentgroup/design-system';
import {
  useContractorDocument,
  useGetContractorInvoice,
} from '@omnipresentgroup/domains';
import { DocumentStatusEnum, DocumentTypeEnum } from '@omnipresentgroup/types';

enum FileAction {
  VIEW = 0,
  DOWNLOAD = 1,
}

const CustomFieldLabel = ({ id, label }: { id: string; label: string }) => {
  return (
    <Typography as="label" size="12" weight="medium" color="subtle" id={id}>
      {label}
    </Typography>
  );
};

type Props = {
  invoiceId: string;
};

export const ContractorInvoiceAttachments = ({ invoiceId }: Props) => {
  const elementId = 'invoice-attachments';

  const { data: invoice } = useGetContractorInvoice({
    id: invoiceId,
    enabled: Boolean(invoiceId),
  });

  const { viewDocument, downloadDocument } = useContractorDocument(
    DocumentTypeEnum.INVOICE,
  );

  const handleFileAction = async ({
    id,
    action,
  }: {
    id: string;
    action: FileAction;
  }) => {
    const attachmentData = invoice?.attachments?.find(
      (attachment) => attachment.id === id,
    );

    switch (attachmentData?.status) {
      case DocumentStatusEnum.PROCESSING: {
        notifyWarning({
          title:
            'The document is being processed and will be possible to access soon.',
        });
        return;
      }
      case DocumentStatusEnum.UPLOADED: {
        switch (action) {
          case FileAction.VIEW: {
            await viewDocument({ id });
            break;
          }
          case FileAction.DOWNLOAD: {
            await downloadDocument({ id });
            break;
          }
        }
        return;
      }
      case DocumentStatusEnum.FAILED: {
        notifyWarning({
          title:
            'The document has failed previously to be uploaded and is not available to view.',
        });
        return;
      }
      default: {
        return;
      }
    }
  };

  return invoice?.attachments && invoice?.attachments.length > 0 ? (
    <Stack gap="8">
      <CustomFieldLabel id={elementId} label="Attachments" />
      <FileCard
        id={`${elementId}-filecard`}
        files={
          invoice?.attachments?.map((attachment) => ({
            id: attachment.id,
            props: new File([], attachment.filename),
          })) || []
        }
        handleFileView={(props) =>
          handleFileAction({ id: props.id || '', action: FileAction.VIEW })
        }
        handleFileDownload={(props) =>
          handleFileAction({
            id: props.id || '',
            action: FileAction.DOWNLOAD,
          })
        }
      />
    </Stack>
  ) : (
    // eslint-disable-next-line react/jsx-no-useless-fragment
    <></>
  );
};
